import { t } from '@lingui/core/macro';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import React from 'react';

import { SolutionTagCounts } from '../../extractors';
import { SolutionTagList } from './SolutionTagList';

/** Groups the tags by severity: errors, then warnings, then info. */
export const SolutionTagsTooltip: React.FunctionComponent<{
    tagCounts: SolutionTagCounts;
}> = ({ tagCounts }) => {
    const { errorTags, warningTags, infoTags } = tagCounts;

    const hasAnyTags = errorTags.length > 0 || warningTags.length > 0 || infoTags.length > 0;

    if (!hasAnyTags) {
        return (
            <Flexbox
                sx={{
                    padding: '4px',
                }}
            >
                <Text variant="body-small" color={colorSystem.neutral[7]}>
                    {t`No solution tags`}
                </Text>
            </Flexbox>
        );
    }

    return (
        <Flexbox
            gap={12}
            sx={{
                flexDirection: 'column',
                padding: '4px',
            }}
        >
            {errorTags.length > 0 && (
                <Flexbox
                    gap={4}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Text variant="h5" color={colorSystem.red[7]}>
                        {t`Errors`}
                    </Text>
                    <SolutionTagList chips={errorTags} />
                </Flexbox>
            )}
            {warningTags.length > 0 && (
                <Flexbox
                    gap={4}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Text variant="h5" color={colorSystem.yellow[7]}>
                        {t`Warnings`}
                    </Text>
                    <SolutionTagList chips={warningTags} />
                </Flexbox>
            )}
            {infoTags.length > 0 && (
                <Flexbox
                    gap={4}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Text variant="h5" color={colorSystem.neutral[7]}>
                        {t`Info`}
                    </Text>
                    <SolutionTagList chips={infoTags} />
                </Flexbox>
            )}
        </Flexbox>
    );
};

/** Shows the counts as "errors / warnings / info". */
export const SolutionTagsCountDisplay: React.FunctionComponent<{
    tagCounts: SolutionTagCounts;
}> = ({ tagCounts }) => {
    const { errorCount, warningCount, infoCount } = tagCounts;

    const hasAnyTags = errorCount > 0 || warningCount > 0 || infoCount > 0;

    if (!hasAnyTags) {
        return (
            <Text variant="body-small" color={colorSystem.neutral[6]}>
                -
            </Text>
        );
    }

    return (
        <Flexbox
            gap={4}
            sx={{
                alignItems: 'center',
            }}
        >
            <Text
                variant="body-small"
                color={errorCount > 0 ? colorSystem.red[6] : colorSystem.neutral[5]}
                style={{ fontWeight: errorCount > 0 ? 600 : 400 }}
            >
                {errorCount}
            </Text>
            <Text variant="body-small" color={colorSystem.neutral[5]}>
                /
            </Text>
            <Text
                variant="body-small"
                color={warningCount > 0 ? colorSystem.yellow[7] : colorSystem.neutral[5]}
                style={{ fontWeight: warningCount > 0 ? 600 : 400 }}
            >
                {warningCount}
            </Text>
            <Text variant="body-small" color={colorSystem.neutral[5]}>
                /
            </Text>
            <Text
                variant="body-small"
                color={infoCount > 0 ? colorSystem.neutral[7] : colorSystem.neutral[5]}
                style={{ fontWeight: infoCount > 0 ? 600 : 400 }}
            >
                {infoCount}
            </Text>
        </Flexbox>
    );
};
